/*
 * Factor Detail page.
 *
 * Deep-dive into one of the five risk-score factors (fail-rate, repeat,
 * stage-depth, training-debt, course-lag). Hero shows current value with
 * a RAG status, a 6-month org trend with industry baseline overlay, a
 * score-impact bar (current vs max possible contribution), top
 * contributors table and a hand-off button back to Sims or Courses.
 *
 * Calculations:
 *   - RAG: green when component <= industry * 0.85, amber when component
 *     <= industry * 1.10, red otherwise.
 *   - trend: the factor's mean on the last day of each of the last 6 months,
 *     from the 'risk' response's daily trend.
 *   - top contributors: the 'users' aggregation, sorted by component DESC.
 *   - score impact: current avg contribution vs (signal.weight) max.
 *
 * Data: every figure is the server's (BD-1245): 'risk' (factor means,
 * contributions, trend), 'risk_benchmark' (platform / industry anchors) and
 * 'users'. With no server value a figure shows "-", never the browser model.
 */
import React, { useMemo } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'motion/react';
import TrendAreaChart from './TrendAreaChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MainCard from '../../../components/cards/MainCard';
import SectionTitle from './SectionTitle';
import DataTable from './DataTable';
import WidgetState from './WidgetState';
import {
    SIGNALS,
    BANDS,
    FACTOR_SOURCE_PAGE,
    SCORE_WINDOW_LABEL,
} from '../riskScore';
import useComparator, { pickAnchorValue } from '../useComparator';
import useDashboardData from '../hooks/useDashboardData';
import { useRiskTrendQuery } from './summary/RiskTrendChart';
import { useRiskBenchmarkQuery } from './summary/BenchmarkPositionCard';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
// Maps a factor id to the per-user field on the `users` aggregation rows.
const FACTOR_FIELD = { 'fail-rate': 'failRate', 'repeat': 'repeat', 'stage-depth': 'stageDepth', 'training-debt': 'trainingDebt', 'course-lag': 'courseLag' };
const FACTOR_AGG_FIELD = { 'fail-rate': 'factorFailRate', 'repeat': 'factorRepeat', 'stage-depth': 'factorStageDepth', 'training-debt': 'factorTrainingDebt', 'course-lag': 'factorCourseLag' };
const FACTOR_CONTRIB_FIELD = { 'fail-rate': 'contribFailRate', 'repeat': 'contribRepeat', 'stage-depth': 'contribStageDepth', 'training-debt': 'contribTrainingDebt', 'course-lag': 'contribCourseLag' };
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthlyFactorTrend(points, field, months = 6) {
    if (!Array.isArray(points) || !field) return [];
    const byMonth = new Map();
    [...points]
        .filter((p) => p && p.day && typeof p[field] === 'number')
        .sort((a, b) => a.day.localeCompare(b.day))
        .forEach((p) => byMonth.set(p.day.slice(0, 7), p));
    return [...byMonth.values()].slice(-months).map((p) => ({
        label: MONTH_LABELS[Number(p.day.slice(5, 7)) - 1],
        value: Math.min(1, Math.max(0, p[field])),
    }));
}

const FACTOR_BENCH_FIELD = {
    'fail-rate':     { industry: 'industryFailRate',     platform: 'platformFailRate' },
    'repeat':        { industry: 'industryRepeat',       platform: 'platformRepeat' },
    'stage-depth':   { industry: 'industryStageDepth',   platform: 'platformStageDepth' },
    'training-debt': { industry: 'industryTrainingDebt', platform: 'platformTrainingDebt' },
    'course-lag':    { industry: 'industryCourseLag',    platform: 'platformCourseLag' },
};

// Generic per-factor detail page. Re-rendered for each of the five score
// drivers by routing `drill('factor-detail', label, { value: '<id>' })`
// to it. Sections, ordered top-to-bottom:
//   1. Hero, headline value + trend delta + RAG
//   2. Trend, 6-month line chart of the org-wide factor mean
//   3. Impact, weighted contribution vs the factor's possible max
//   4. Top contributors, users driving the factor up the most
//   5. Hand-off, "View on Simulations / Courses" affordance into the
//      source page where the activity behind the factor lives

// Chart axis units per factor format; anything else plots as a percentage.
const CHART_UNITS = {
    days:  { scale: 30, dp: 1, suffix: 'd' },
    depth: { scale: 1,  dp: 2, suffix: '' },
};
const PERCENT_UNIT = { scale: 100, dp: 0, suffix: '%' };

function formatValue(component, anchor) {
    if (!anchor) return `${Math.round(component * 100)}%`;
    if (anchor.format === 'percent') return `${Math.round(component * 100)}%`;
    // At the cap the true lag is "30 or more days" - label it so, rather than 30.0d.
    if (anchor.format === 'days')    return component >= 1 ? '30+ days' : `${(component * 30).toFixed(1)}d`;
    if (anchor.format === 'depth')   return component.toFixed(2);
    return `${Math.round(component * 100)}%`;
}

// Month-over-month change in the factor's *own* value, expressed in the
// factor's display unit (percentage / days / depth). Deliberately NOT in
// "pts": "pts" is reserved across the risk surface for points on the 0-100
// score, and this delta is a change in the factor value, not the score.
function formatDelta(component, anchor) {
    if (!anchor) return `${Math.round(component * 100)}%`;
    if (anchor.format === 'percent') return `${Math.round(component * 100)}%`;
    if (anchor.format === 'days')    return component >= 1 ? '30+ days' : `${(component * 30).toFixed(1)} days`;
    if (anchor.format === 'depth')   return component.toFixed(2);
    return `${Math.round(component * 100)}%`;
}

export default function FactorDetailPage({ factorId, drill, scope }) {
    const theme = useTheme();
    const [comparator] = useComparator();

    const signal = SIGNALS.find((s) => s.id === factorId);
    const source = FACTOR_SOURCE_PAGE[factorId];
    // Every query reads server-side, not the client-pooled ./data, so a Tenancy
    // filter is forwarded to pool the selected child tenancies' users. With no
    // Tenancy filter riskQ / benchQ are the Risk page's own requests (shared cache).
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const factorUsersQ = useDashboardData({ metric: 'simulations', aggregation: 'users', limit: 5000, scope: tenancyScope });
    const riskQ = useRiskTrendQuery(tenancyScope);
    const benchQ = useRiskBenchmarkQuery(tenancyScope);
    const bench = benchQ.data?.riskBenchmark;
    const serverMean = riskQ.data?.risk?.[FACTOR_AGG_FIELD[factorId]];
    const serverContribution = riskQ.data?.risk?.[FACTOR_CONTRIB_FIELD[factorId]];

    // BD-1245: the 'risk' response carries the daily trend too.
    const trend = useMemo(
        () => monthlyFactorTrend(riskQ.data?.riskTrend, FACTOR_AGG_FIELD[factorId]),
        [riskQ.data, factorId],
    );
    const contributors = useMemo(() => {
        const field = FACTOR_FIELD[factorId];
        const ru = factorUsersQ.data?.riskUsers;
        if (Array.isArray(ru) && field) {
            // component > 0 only: a user with a zero component isn't driving the
            // factor, so they must not pad out the top-10 in small tenancies.
            return ru
                .map((u) => ({ email: u.email, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, sent: u.sent || 0, clicks: u.clicked || 0, secondStage: u.secondStage || 0, reported: u.reported || 0, coursesSent: u.coursesSent || 0, coursesCompleted: u.coursesCompleted || 0, coursesInWindow: u.coursesInWindow ?? null, coursesOverdue: u.coursesOverdue ?? null, component: u[field], contribution: (u[field] || 0) * (signal?.weight || 0) }))
                .filter((u) => u.component > 0)
                .sort((a, b) => b.component - a.component).slice(0, 10);
        }
        return [];
    }, [factorId, factorUsersQ.data, signal]);

    if (!signal) {
        return (
            <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary', fontSize: 12 }}>
                Unknown factor: {String(factorId)}
            </Box>
        );
    }

    // Format / direction / target are constants on SIGNALS; the industry and
    // platform anchors come only from risk_benchmark (BD-1245).
    const benchFields = FACTOR_BENCH_FIELD[factorId];
    const anchor = {
        ...signal.anchor,
        industry: (bench && benchFields ? bench[benchFields.industry] : null) ?? null,
        platform: (bench && benchFields ? bench[benchFields.platform] : null) ?? null,
    };
    const currentValue = typeof serverMean === 'number' ? serverMean : null;
    const previousValue = trend.length >= 2 ? trend[trend.length - 2].value : currentValue;
    const periodDelta = currentValue != null ? currentValue - previousValue : 0;
    const improving = anchor?.goodBelow ? periodDelta < 0 : periodDelta > 0;

    // RAG matches the FactorStrip convention so a reader who clicked
    // through from a green card doesn't suddenly see red on landing.
    const rag = (() => {
        if (!anchor || currentValue == null) return null;
        const cmp = pickAnchorValue(anchor, comparator) ?? 0;
        if (cmp === 0) return null;
        const ratio = currentValue / cmp;
        const goodBelow = anchor.goodBelow !== false;
        if (goodBelow) {
            if (ratio <= 0.85) return 'green';
            if (ratio <= 1.10) return 'amber';
            return 'red';
        }
        if (ratio >= 1.15) return 'green';
        if (ratio >= 0.90) return 'amber';
        return 'red';
    })();
    const ragColor = rag === 'green' ? '#66BB6A' : rag === 'amber' ? '#FFCA28' : rag === 'red' ? '#FF5252' : theme.palette.primary.main;

    // Score impact: how many points this factor is currently
    // contributing toward the org's score, and what the contribution
    // *could* drop to if the component fell to industry.
    const currentContribution = typeof serverContribution === 'number' ? serverContribution : null;
    const contributionText = currentContribution != null ? currentContribution.toFixed(1) : '-';
    const maxContribution = signal.weight;
    // Industry has no data source on the platform, so the Industry chip /
    // baseline / contribution bar render only when industry data exists
    // (matching the flag-off non-render rule). The Platform bar below is the
    // designed sibling element and carries the real benchmark anchor.
    // anchorRef is the best available benchmark (industry, else platform),
    // used only for colour thresholds and headroom — never as a visible label.
    const anchorRef = anchor ? (anchor.industry ?? anchor.platform) : null;
    const industryContribution = anchor && anchor.industry != null ? signal.weight * anchor.industry : null;
    const platformContribution = anchor && anchor.platform != null ? signal.weight * anchor.platform : null;
    const headroom = anchorRef != null && currentContribution != null
        ? Math.max(0, currentContribution - signal.weight * anchorRef)
        : 0;

    // Trend chart rows, expressed in the factor's display unit so the axis
    // reads as percent / days / depth depending on the factor.
    const unit = CHART_UNITS[anchor?.format] || PERCENT_UNIT;
    const toUnit = (v) => +(v * unit.scale).toFixed(unit.dp);
    const showIndustry = anchor?.industry != null;
    const trendRows = trend.map((p) => ({
        label: p.label,
        org: toUnit(p.value),
        industry: showIndustry ? toUnit(anchor.industry) : undefined,
    }));
    const trendLines = [
        { key: 'org', name: 'Org', color: ragColor, width: 3, stops: [[0, ragColor, 0.22], [60, ragColor, 0.06], [100, ragColor, 0]] },
        ...(showIndustry ? [{ key: 'industry', name: 'Industry', color: '#6B7591', width: 2, dashed: true }] : []),
    ];

    // Course factors show course counts; sim factors show sim counts.
    const isCourseFactor = factorId === 'training-debt' || factorId === 'course-lag';
    // Course factors show counts over the SAME 6-month window the component is
    // computed on (coursesInWindow / coursesOverdue). The all-time Sent/Completed
    // columns can't explain a windowed percentage - they were the reason a user
    // with 9 outstanding appeared to be at 40%. All-time stays available in the
    // column picker for context.
    const countColumns = isCourseFactor
        ? [
            { id: 'coursesInWindow', label: 'Assigned (6m)', defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.coursesInWindow ?? u.coursesSent, align: 'right' },
            { id: 'coursesOverdue',  label: 'Overdue (6m)',  defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.coursesOverdue ?? 0, align: 'right' },
            { id: 'coursesSent',      label: 'Sent (all time)',      sortable: true, type: 'number', accessor: (u) => u.coursesSent, align: 'right' },
            { id: 'coursesCompleted', label: 'Completed (all time)', sortable: true, type: 'number', accessor: (u) => u.coursesCompleted, align: 'right' },
        ]
        : [
            { id: 'sent',         label: 'Sent',             defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
            { id: 'clicks',       label: 'Clicks',           defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.clicks, align: 'right' },
            { id: 'secondStage',  label: '2nd stage clicks', defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.secondStage, align: 'right' },
            { id: 'reported',     label: 'Reported',         defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.reported, align: 'right' },
        ];
    const contributorColumns = [
        { id: 'name',         label: 'Name',             always: true,    sortable: true, type: 'string', accessor: (u) => u.name },
        { id: 'email',        label: 'Email',            defaultOn: true, sortable: true, type: 'string', accessor: (u) => u.email },
        ...countColumns,
        { id: 'component',    label: signal.label,   defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.component, align: 'right',
          // Stage depth is the worst SINGLE test (clicked link = 50%, landing
          // page = 100%), so label the stage rather than showing a bare percent
          // that reads as contradicting a 0 in the 2nd-stage-clicks column.
          render: (u) => <span style={{ fontWeight: 700, color: u.component > (anchor?.industry ?? anchor?.platform ?? 0) ? '#FF5252' : '#66BB6A' }}>{
              // Per-user stage depth is a stage, not a rate - the percentage only
              // means something once averaged, so the user rows name the stage.
              factorId === 'stage-depth'
                  ? (u.component >= 1 ? '2nd stage' : u.component >= 0.5 ? 'Clicked link' : 'No fail')
                  : formatValue(u.component, anchor)
          }</span> },
        { id: 'contribution', label: 'Score impact', defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.contribution, align: 'right',
          format: (v) => `+${v.toFixed(1)} score pts` },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Hero */}
            <motion.div variants={fadeUp}>
                <WidgetState queries={[riskQ, benchQ]}>
                <Box sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '14px',
                    border: '1px solid', borderColor: 'divider',
                    background: `linear-gradient(135deg, ${ragColor}1A 0%, ${ragColor}05 55%, transparent 100%)`,
                    p: { xs: 2.5, md: 3.5 },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                                Score driver - {signal.weight}% of score
                            </Typography>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em', mt: 0.25 }}>
                                {signal.label}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.55, mt: 0.5, maxWidth: 720 }}>
                                {signal.description}
                            </Typography>
                        </Box>

                        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 44, fontWeight: 800, color: ragColor, letterSpacing: '-0.03em', lineHeight: 1 }}>
                                {currentValue != null ? formatValue(currentValue, anchor) : '-'}
                            </Typography>
                            {currentValue != null && trend.length >= 2 && (
                                <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    px: 1, py: '3px',
                                    borderRadius: '999px',
                                    bgcolor: improving ? 'rgba(102,187,106,.14)' : 'rgba(255,82,82,.14)',
                                    color: improving ? '#388E3C' : '#E53935',
                                    fontSize: 11, fontWeight: 700,
                                }}>
                                    {improving ? '↓' : '↑'} {formatDelta(Math.abs(periodDelta), anchor)} vs last month
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {anchor && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2.5 }}>
                            {anchor.industry != null && (
                                <HeroChip label="Industry"       value={formatValue(anchor.industry, anchor)} />
                            )}
                            <HeroChip label="Your score impact"  value={currentContribution != null ? `+${contributionText} score pts` : '-'} />
                            <HeroChip label="Max possible"       value={`+${maxContribution} score pts`} />
                            <HeroChip label="Window"             value={`Rolling ${SCORE_WINDOW_LABEL}`} />
                        </Box>
                    )}
                </Box>
                </WidgetState>
            </motion.div>

            {/* Trend */}
            <SectionTitle>Trend over time</SectionTitle>
            <motion.div variants={fadeUp}>
                <WidgetState queries={[riskQ, benchQ]}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                {signal.label} over time
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Org-wide mean across 6 months - {anchor?.goodBelow ? 'lower is better' : 'higher is better'}{showIndustry ? ` - industry baseline ${formatValue(anchor.industry, anchor)}` : ''}
                            </Typography>
                        </Box>
                        {showIndustry && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                    <Box sx={{ width: 20, height: 2, bgcolor: ragColor, borderRadius: '1px' }} /> Org
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                    <Box sx={{ width: 20, height: 2, backgroundImage: 'repeating-linear-gradient(90deg,#6B7591 0,#6B7591 4px,transparent 4px,transparent 8px)' }} /> Industry
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ px: 3, pb: 2, height: 240 }}>
                        <TrendAreaChart
                            key={factorId}
                            data={trendRows}
                            xKey="label"
                            series={trendLines}
                            yAxis={{ tickFormatter: (v) => `${v}${unit.suffix}` }}
                            animate
                        />
                    </Box>
                </MainCard>
                </WidgetState>
            </motion.div>

            {/* Impact */}
            <SectionTitle>Score impact</SectionTitle>
            <motion.div variants={fadeUp}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={7}>
                        <WidgetState queries={[riskQ, benchQ]} fill>
                        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                        Where this sits in your org score
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                        Each score point adds 1% to your org score. This factor's weight caps its contribution at {maxContribution} score pts ({maxContribution}%).
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                                                Your org
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>
                                                {contributionText} {currentContribution != null && <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>score pts ({Math.round((currentContribution / maxContribution) * 100)}% of ceiling)</Box>}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ position: 'relative', height: 12, borderRadius: '4px', overflow: 'hidden', bgcolor: 'divider', border: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{
                                                position: 'absolute', left: 0, top: 0, bottom: 0,
                                                width: `${Math.min(100, ((currentContribution ?? 0) / maxContribution) * 100)}%`,
                                                bgcolor: ragColor,
                                            }} />
                                        </Box>
                                    </Box>

                                    {industryContribution != null && (
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                                                    Industry average
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>
                                                    {industryContribution.toFixed(1)} <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>score pts ({Math.round((industryContribution / maxContribution) * 100)}% of ceiling)</Box>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ position: 'relative', height: 12, borderRadius: '4px', overflow: 'hidden', bgcolor: 'divider', border: '1px solid', borderColor: 'divider' }}>
                                                <Box sx={{
                                                    position: 'absolute', left: 0, top: 0, bottom: 0,
                                                    width: `${Math.min(100, (industryContribution / maxContribution) * 100)}%`,
                                                    bgcolor: 'text.primary',
                                                    opacity: 0.55,
                                                }} />
                                            </Box>
                                        </Box>
                                    )}

                                    {platformContribution != null && (
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                                                    Platform average
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>
                                                    {platformContribution.toFixed(1)} <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>score pts ({Math.round((platformContribution / maxContribution) * 100)}% of ceiling)</Box>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ position: 'relative', height: 12, borderRadius: '4px', overflow: 'hidden', bgcolor: 'divider', border: '1px solid', borderColor: 'divider' }}>
                                                <Box sx={{
                                                    position: 'absolute', left: 0, top: 0, bottom: 0,
                                                    width: `${Math.min(100, (platformContribution / maxContribution) * 100)}%`,
                                                    bgcolor: 'text.primary',
                                                    opacity: 0.3,
                                                }} />
                                            </Box>
                                        </Box>
                                    )}

                                    {industryContribution != null && currentContribution != null && (
                                        <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                                            If your {signal.label.toLowerCase()} matched industry, this factor would add <b>{industryContribution.toFixed(1)} score pts</b> instead of {contributionText}.
                                            That's <b style={{ color: headroom > 0 ? '#FF5252' : '#66BB6A' }}>{headroom.toFixed(1)} score pts</b> of headroom on your score.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </MainCard>
                        </WidgetState>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <WidgetState queries={[riskQ]} fill>
                        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                        Where the bands sit
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                        Your weighted contribution mapped onto the score's band ranges
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                                    {BANDS.map((b) => {
                                        const isYourBand = currentContribution != null && currentContribution >= (b.min * signal.weight / 100) && currentContribution <= (b.max * signal.weight / 100);
                                        return (
                                            <Box key={b.name} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1,
                                                px: 1, py: 0.75,
                                                borderRadius: '6px',
                                                border: '1px solid',
                                                borderColor: isYourBand ? b.border : 'divider',
                                                bgcolor: isYourBand ? b.bg : 'transparent',
                                                opacity: isYourBand ? 1 : 0.6,
                                            }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color }} />
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: isYourBand ? b.textColor : 'text.secondary' }}>
                                                    {b.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 'auto' }}>
                                                    {b.min}-{b.max}
                                                </Typography>
                                                {isYourBand && (
                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: b.textColor }}>You</Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </MainCard>
                        </WidgetState>
                    </Grid>
                </Grid>
            </motion.div>

            {/* Top contributors */}
            <SectionTitle>Top contributors</SectionTitle>
            {factorId === 'course-lag' && (
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: -0.5 }}>
                    Course lag averages every course assigned in the rolling 6-month window: completed courses at their send-to-complete time,
                    still-open courses at how long they have been outstanding. Both are capped at 30 days, so anything at the cap shows as 30+ days.
                </Typography>
            )}
            {factorId === 'training-debt' && (
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: -0.5 }}>
                    Training debt is overdue courses divided by courses assigned in the rolling 6-month window (a course counts as overdue 14 days after it was sent).
                    The Assigned / Overdue columns use that same window, so they reconcile with the percentage.
                </Typography>
            )}
            {factorId === 'stage-depth' && (
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: -0.5 }}>
                    Stage depth is each user's worst single test: clicking the email link counts as half, continuing to the landing page counts as full.
                    "Clicked link" means they clicked but never made a 2nd-stage click. The org percentage above averages these across users.
                </Typography>
            )}
            <motion.div variants={fadeUp}>
                <WidgetState queries={[factorUsersQ]}>
                <DataTable
                    title={`Users driving ${signal.label.toLowerCase()}`}
                    subtitle={`Ranked by their ${signal.label.toLowerCase()} component value. Click View for detail.`}
                    columns={contributorColumns}
                    rows={contributors}
                    rowKey={(u) => u.email}
                    onRowClick={(u) => drill && drill('user-detail', u.name, { type: 'user', value: u.email })}
                    initialSort={{ by: 'component', dir: 'desc' }}
                    storageKey={`rbx-factor-${factorId}-cols-v2`}
                    pagination={false}
                    emptyMessage="No contributing users in window"
                />
                </WidgetState>
            </motion.div>

            {/* Hand-off */}
            {source && (
                <motion.div variants={fadeUp} style={{ marginTop: 8 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                        p: 2.5,
                        borderRadius: '10px',
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        flexWrap: 'wrap',
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                See the activity behind this factor
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                                Open {source.label} to investigate the raw data that drives {signal.label.toLowerCase()}.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined" color="secondary"
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                            onClick={() => drill && drill(source.page, source.label)}
                            sx={{
                                borderRadius: '6px', fontSize: 12, fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.04em',
                                px: 1.75, py: '6px',
                                borderColor: 'secondary.main', color: 'secondary.main',
                                '&:hover': { borderColor: 'secondary.main', backgroundColor: 'secondary.main', color: 'common.white' },
                            }}
                        >
                            Open {source.label}
                        </Button>
                    </Box>
                </motion.div>
            )}
        </motion.div>
    );
}

function HeroChip({ label, value }) {
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'baseline', gap: 0.75,
            px: 1.25, py: 0.5,
            borderRadius: '999px',
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
        }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                {value}
            </Typography>
        </Box>
    );
}
